// React
import { gql, useMutation, useQuery } from "@apollo/client";
import { useContext, useMemo, useState } from "react";

// Components
import { Card, Collapse, Grid, Icon, Menu, MenuItem, Select, Tooltip } from "@mui/material";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { ConfirmarContext } from "context/ConfirmarContext";
//Data
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import { getUproColor } from "utils/colors";
import facturasData from "./data/facturasData";
import ModalAddFactura from "./ModalAdd"; // Importar el modal
import ModalDetallesFactura from "./ModalDetalles";

function Facturas() {
  const [openModalAdd, setOpenModalAdd] = useState(false); // Estado para controlar el modal
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openFilters, setOpenFilters] = useState(false);
  const [page, setPage] = useState(1);
  const { confirmar } = useContext(ConfirmarContext);
  const [filter, setFilter] = useState({});
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "id",
    orden: "DESC",
  });
  const { handleSnackbar } = useContext(MessageManager);
  const { columns, getRows } = facturasData;
  const SAVE_FACTURA = gql`
    mutation SaveFactura($input: FacturaInput!) {
      saveFactura(input: $input) {
        id
      }
    }
  `;
  const [saveFactura, { loading: loadingSave }] = useMutation(SAVE_FACTURA);
  const DELETE_FACTURA = gql`
    mutation DeleteFactura($id: ID!) {
      deleteFactura(id: $id) {
        id
      }
    }
  `;
  const [deleteFactura, { loading: loadingDelete }] = useMutation(DELETE_FACTURA, {
    onCompleted: () => {
      handleSnackbar("Factura eliminada correctamente", "success");
      refetch();
    },
    onError: (error) => {
      handleSnackbar(error.message, "error");
    },
  });

  const GET_PROVEEDORES = gql`
    query GetProveedores {
      proveedores {
        id
        razonSocial
        nombre
        apellido
      }
    }
  `;
  const { data: dataProveedores, loading: loadingProveedores } = useQuery(GET_PROVEEDORES, {
    fetchPolicy: "network-only",
  });
  const { data, loading, error, refetch } = useQuery(
    gql`
      query facturas($filter: JSON, $search: String, $page: Int, $order: Order) {
        paginationInfo {
          pages
          total
        }
        facturas(
          filter: $filter
          search: $search
          pagination: { page: $page, limit: 10 }
          order: $order
        ) {
          id
          estado
          proveedor {
            id
            tipoProveedor
            razonSocial
            nombre
            apellido
          }
          numeroFactura
          fechaFactura
          tipoFactura
          monto
          files
          movimientos {
            id
            tipo
            fechaEntrega
            movimientoDetalles {
              materiaPrima {
                nombre
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        filter: filter || null,
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
      },
      fetchPolicy: "network-only",
    }
  );

  const handleFilter = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value === "0" ? undefined : value,
    }));
  };

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedFactura(item); // Guardar la factura seleccionada
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const [openModalDetalles, setOpenModalDetalles] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);

  const handleOpenDetalles = (factura) => {
    setSelectedFactura(factura);
    setOpenModalDetalles(true);
  };

  const handleCloseDetalles = () => {
    setSelectedFactura(null);
    setOpenModalDetalles(false);
  };

  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          handleCloseMenu();
          setOpenModalDetalles(true); // Abrir el modal de detalles
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Ver Detalle</SoftTypography>
          <Icon
            fontSize="small"
            sx={{ verticalAlign: "middle", ml: 1, color: getUproColor("uproPink") }}
          >
            visibility
          </Icon>
        </SoftBox>
      </MenuItem>

      <MenuItem
        onClick={() => {
          confirmar({
            title: "Eliminar Factura",
            message: "¿Está seguro de que desea eliminar la factura?",
            icon: "delete",
            func: () => {
              handleCloseMenu();
              deleteFactura({
                variables: {
                  id: selectedFactura.id,
                },
              });
            },
          });
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Eliminar</SoftTypography>
          <Icon
            fontSize="small"
            sx={{ verticalAlign: "middle", ml: 1, color: getUproColor("error") }}
          >
            delete
          </Icon>
        </SoftBox>
      </MenuItem>

      {selectedFactura?.estado === "PENDIENTE" && (
        <MenuItem
          onClick={() => {
            confirmar({
              title: "Actualizar Estado",
              message: "¿Está seguro de que desea actualizar el estado de la factura a pagada?",
              icon: "check_circle",
              func: () => {
                handleCloseMenu();
                saveFactura({
                  variables: {
                    input: {
                      id: selectedFactura.id,
                      estado: "PAGADO",
                    },
                  },
                })
                  .then(() => {
                    handleSnackbar("Estado actualizado correctamente", "success");
                    refetch();
                  })
                  .catch((error) => {
                    handleSnackbar(error.message, "error");
                  });
              },
            });
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <SoftTypography variant="p">Pagar</SoftTypography>
            <Icon
              fontSize="small"
              sx={{ verticalAlign: "middle", ml: 1, color: getUproColor("uproGreen") }}
            >
              check_circle
            </Icon>
          </SoftBox>
        </MenuItem>
      )}
    </Menu>
  );

  const allRows = useMemo(() => {
    if (data) {
      setPagesInfo(data.paginationInfo);
      return getRows(data.facturas, handleOpenMenu);
    }
  }, [data]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox
                  display={{
                    xs: "flex-row",
                    sm: "flex",
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                >
                  <SoftBox
                    flex={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <SoftTypography variant="h6">Facturas</SoftTypography>
                    <Tooltip title="Filtrar">
                      <SoftButton
                        color="dark"
                        circular
                        iconOnly
                        onClick={() => setOpenFilters(!openFilters)}
                      >
                        <Icon>{openFilters ? "filter_list_off" : "filter_list"}</Icon>
                      </SoftButton>
                    </Tooltip>
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                    flexDirection={{ xs: "column-reverse", sm: "row" }}
                    gap={1}
                    mt={{
                      xs: 2,
                      sm: 0,
                    }}
                  >
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                    >
                      <SoftInput
                        placeholder="Escribe aquí..."
                        icon={{ component: "search", direction: "left" }}
                        onChange={handleSearch}
                      />
                    </SoftBox>

                    <SoftBox width={{ xs: "100%", sm: "auto" }}>
                      <Tooltip title="Agregar Factura" placement="top">
                        <SoftButton
                          color="uproPink"
                          circular
                          fullWidth
                          onClick={() => setOpenModalAdd(true)} // Abrir el modal
                        >
                          <Icon>add</Icon>
                          &nbsp;Agregar
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
                <Collapse in={openFilters}>
                  <SoftBox
                    display="flex"
                    justifyContent="flex-end"
                    mt={1}
                    gap={1}
                    flexDirection={{ xs: "column", sm: "row" }}
                  >
                    <SoftBox width={{ xs: "100%", sm: "auto" }}>
                      <Select
                        fullWidth
                        name="estado"
                        value={filter.estado || "0"}
                        onChange={handleFilter}
                      >
                        <MenuItem value="0">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" color="secondary">
                              all_inbox
                            </Icon>
                            Todos los Estados
                          </SoftBox>
                        </MenuItem>
                        <MenuItem value="PENDIENTE">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" color="warning">
                              pending
                            </Icon>
                            Pendiente
                          </SoftBox>
                        </MenuItem>
                        <MenuItem value="PAGADO">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" color="success">
                              check_circle
                            </Icon>
                            Pagado
                          </SoftBox>
                        </MenuItem>
                      </Select>
                    </SoftBox>

                    <SoftBox width={{ xs: "100%", sm: "auto" }}>
                      <Select
                        fullWidth
                        name="idProveedor"
                        value={filter.idProveedor || "0"}
                        onChange={handleFilter}
                      >
                        <MenuItem value="0">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" color="secondary">
                              all_inbox
                            </Icon>
                            Todos los Proveedores
                          </SoftBox>
                        </MenuItem>
                        {dataProveedores?.proveedores?.map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            <SoftBox display="flex" alignItems="center" gap={1}>
                              <Icon
                                fontSize="small"
                                sx={{
                                  color: getUproColor("uproPink"),
                                }}
                              >
                                account_circle
                              </Icon>
                              {item.razonSocial || `${item.nombre} ${item.apellido}`}
                            </SoftBox>
                          </MenuItem>
                        ))}
                      </Select>
                    </SoftBox>
                    <SoftBox width={{ xs: "100%", sm: "auto" }}>
                      <Select
                        fullWidth
                        name="tipoFactura"
                        value={filter.tipoFactura || "0"}
                        onChange={handleFilter}
                      >
                        <MenuItem value="0">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" color="secondary">
                              all_inbox
                            </Icon>
                            Todos los Tipos de Factura
                          </SoftBox>
                        </MenuItem>
                        <MenuItem value="A">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <SoftButton color="uproGreen" circular iconOnly size="small">
                              A
                            </SoftButton>
                            Factura A
                          </SoftBox>
                        </MenuItem>
                        <MenuItem value="B">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <SoftButton color="warning" circular iconOnly size="small">
                              B
                            </SoftButton>
                            Factura B
                          </SoftBox>
                        </MenuItem>
                        <MenuItem value="C">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <SoftButton color="dark" circular iconOnly size="small">
                              C
                            </SoftButton>
                            Factura C
                          </SoftBox>
                        </MenuItem>
                      </Select>
                    </SoftBox>
                  </SoftBox>
                </Collapse>
              </Grid>
            </Grid>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <Loading />
              ) : allRows?.length > 0 ? (
                <DataTable
                  columns={columns}
                  rows={allRows}
                  order={orderBy}
                  setOrder={handleOrderBy}
                />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={data?.facturas?.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
          </Card>
          {renderMenu}
          <ModalDetallesFactura
            open={openModalDetalles}
            handleClose={handleCloseDetalles}
            factura={selectedFactura}
          />
        </SoftBox>
      </SoftBox>

      {/* Modal para agregar factura */}
      <ModalAddFactura
        open={openModalAdd}
        handleClose={() => setOpenModalAdd(false)}
        refetchParent={refetch}
      />
    </DashboardLayout>
  );
}

export default Facturas;
